import { Link } from 'react-router-dom';
import type {
  ActivityOverviewCount,
  ActivityOverviewDimension,
  ActivityOverviewDomain,
  ActivityOverviewStatusFilter,
} from '@sparkapp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BreakdownBarList } from './BreakdownBarList';
import { CatalogueHealthStrip } from './CatalogueHealthStrip';
import { DomainSkillCoverage } from './DomainSkillCoverage';
import { activityListHref } from './activityListHref';

interface DomainDetailProps {
  domain: ActivityOverviewDomain;
  totalActivities: number;
  status: ActivityOverviewStatusFilter;
  preferences: ActivityOverviewDimension[]; // titles, zipped with domain.preferenceCounts
  packs: ActivityOverviewDimension[];
  playTypes: ActivityOverviewDimension[];
}

function DimensionCard({
  title,
  dims,
  counts,
  hrefFor,
}: {
  title: string;
  dims: ActivityOverviewDimension[];
  counts: ActivityOverviewCount[];
  hrefFor: (id: number) => string;
}) {
  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader>
        <CardTitle className="text-heading-4 text-pine-ink">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {dims.length === 0 ? (
          <p className="text-sm text-pine-ink-soft">None configured.</p>
        ) : (
          <BreakdownBarList
            rows={dims.map((dim, i) => ({
              key: String(dim.id),
              label: <span className="truncate">{dim.title}</span>,
              primary: counts[i]?.activityCount ?? 0,
              href: hrefFor(dim.id),
            }))}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** One domain's picture: skill coverage (primary vs cross-domain), health and per-dimension counts. */
export function DomainDetail({
  domain,
  totalActivities,
  status,
  preferences,
  packs,
  playTypes,
}: DomainDetailProps) {
  const share = totalActivities > 0 ? Math.round((domain.activityCount / totalActivities) * 100) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-heading-3 text-pine-ink">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: domain.style?.strokeColor ?? 'var(--pine-pine)' }}
          />
          <span className="font-semibold">{domain.title}</span>
          <span className="text-body text-pine-ink-soft tabular-nums">
            · {domain.activityCount} {domain.activityCount === 1 ? 'activity' : 'activities'} ·{' '}
            {share === null ? '—' : `${share}%`} of catalogue
          </span>
        </h2>
        <Link
          to={activityListHref(status, { domainId: domain.id })}
          className="text-sm font-medium text-pine-pine hover:underline"
        >
          View activities →
        </Link>
      </div>

      <CatalogueHealthStrip health={domain.health} noActiveDomainCount={0} />

      <DomainSkillCoverage domain={domain} status={status} />

      <div className="grid gap-6 lg:grid-cols-3">
        <DimensionCard
          title="Preferences"
          dims={preferences}
          counts={domain.preferenceCounts}
          hrefFor={(id) => activityListHref(status, { domainId: domain.id, preferenceId: id })}
        />
        <DimensionCard
          title="Packs"
          dims={packs}
          counts={domain.packCounts}
          hrefFor={(id) => activityListHref(status, { domainId: domain.id, physicalDeckId: id })}
        />
        <DimensionCard
          title="Play types"
          dims={playTypes}
          counts={domain.playTypeCounts}
          hrefFor={(id) => activityListHref(status, { domainId: domain.id, playTypeId: id })}
        />
      </div>
    </div>
  );
}
